'use client';

import {articleTestIds} from '@shared/Articles/ArticleTestIds';
import ProxyImage from '@shared/ProxyImage/ProxyImage';
import classNames from '@sindresorhus/class-names';
import {useCallback, useMemo, useState} from 'react';
import {Swiper as ISwiper} from 'swiper';
import 'swiper/css';
import {Navigation} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';

import {
  SLIDER_DESKTOP_HORIZONTAL_COUNT,
  SLIDER_DESKTOP_VERTICAL_COUNT,
  SLIDER_MOBILE_HORIZONTAL_COUNT,
  SLIDER_MOBILE_VERTICAL_COUNT,
  SLIDER_SINGLE_ITEM_COUNT,
} from '@shared/constants';

import {useIsMobile} from '@utils/hooks/useIsMobile';
import useSwiperProgressState from '@utils/hooks/useSwiperProgressState';

import SlideControlButton from '../parts/SlideControlButton';
import styles from './ArticleScreenshotsSwiper.module.scss';
import ScreenshotsModal from './ScreenshotsModal/ScreenshotsModal';
import {Props} from './types';

const ArticleScreenshotsSwiper = ({type, screenShotsList}: Props) => {
  const [modalState, setModalState] = useState({
    isOpen: false,
    initialSlide: 0,
  });
  const [swiperInstance, setSwiperInstance] = useState<ISwiper>();

  const {swiperState, onProgress} = useSwiperProgressState();

  const isMobile = useIsMobile();

  const slidesCount = useMemo(() => {
    const hasOnlyOneScreenshot =
      screenShotsList?.length === SLIDER_SINGLE_ITEM_COUNT;

    if (hasOnlyOneScreenshot) {
      return SLIDER_SINGLE_ITEM_COUNT;
    }

    if (isMobile) {
      return type === 'vertical'
        ? SLIDER_MOBILE_VERTICAL_COUNT
        : SLIDER_MOBILE_HORIZONTAL_COUNT;
    }

    return type === 'vertical'
      ? SLIDER_DESKTOP_VERTICAL_COUNT
      : SLIDER_DESKTOP_HORIZONTAL_COUNT;
  }, [isMobile, type, screenShotsList]);

  const onCloseModal = useCallback(
    () => setModalState({isOpen: false, initialSlide: 0}),
    [],
  );

  return (
    <div className={styles.swiper}>
      {!swiperState?.isBeginning && !isMobile && (
        <SlideControlButton
          standardPosition
          onClick={() => swiperInstance?.slidePrev()}
          data-testid={articleTestIds.proStoreSwiperPrevButton}
          direction="prev"
        />
      )}
      <Swiper
        onSwiper={(swiper) => {
          setSwiperInstance(swiper);
        }}
        modules={[Navigation]}
        onProgress={onProgress}
        slidesPerView="auto"
      >
        {!!screenShotsList?.length &&
          screenShotsList.map((elem, index) => (
            <SwiperSlide
              key={elem.url}
              className={classNames(
                styles.slide,
                styles[type],
                slidesCount === SLIDER_SINGLE_ITEM_COUNT && styles.single,
              )}
            >
              <ProxyImage
                imageData={elem}
                alt="screenshot"
                width={elem.width}
                height={elem.height}
                className={classNames(
                  styles[type],
                  slidesCount === SLIDER_SINGLE_ITEM_COUNT && styles.single,
                )}
                onClick={() =>
                  setModalState({isOpen: true, initialSlide: index})
                }
              />
            </SwiperSlide>
          ))}
      </Swiper>
      {!swiperState?.isEnd && !isMobile && (
        <SlideControlButton
          standardPosition
          onClick={() => swiperInstance?.slideNext()}
          testId={articleTestIds.proStoreSwiperNextButton}
          direction="next"
        />
      )}

      {modalState.isOpen && (
        <ScreenshotsModal
          initialSlide={modalState.initialSlide}
          screenShotsList={screenShotsList}
          type={type}
          onClose={onCloseModal}
        />
      )}
    </div>
  );
};

export default ArticleScreenshotsSwiper;
